import { useEffect, useMemo, useState } from "react";
import { Briefcase, Star, User, X } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { supabase } from "@/integrations/supabase/client";
import {
  FacetMultiSelect,
  FieldLabel,
  SegmentedFilter,
} from "./filter-controls";
import type {
  AtributoFiltro,
  EvaluationFilterValues,
  StatusAvaliacao,
} from "@/types/freelancer";

interface EvaluationFiltersV2Props {
  filters: EvaluationFilterValues;
  onFiltersChange: (filters: EvaluationFilterValues) => void;
}

interface AvailableAttribute {
  label: string;
  slug: string;
  formulario: string;
}

const STATUS_OPTIONS = [
  { value: "sim", label: "Sim" },
  { value: "nao", label: "Não" },
];

const DEFAULT_MIN_VALUE = 5;

interface MinScoreSliderProps {
  icon: React.ReactNode;
  label: string;
  hint: string;
  value: number | undefined;
  onChange: (value: number | undefined) => void;
}

function MinScoreSlider({ icon, label, hint, value, onChange }: MinScoreSliderProps) {
  return (
    <div>
      <FieldLabel>
        {icon}
        {label}
      </FieldLabel>
      <div className="flex items-center gap-3" title={hint}>
        <Slider
          value={[value ?? 0]}
          onValueChange={(next) => onChange(next[0] > 0 ? next[0] : undefined)}
          max={10}
          step={0.5}
          className="flex-1"
        />
        <span className="w-10 rounded bg-muted px-1.5 py-0.5 text-center font-mono text-xs">
          {value ?? 0}
        </span>
      </div>
      <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>
    </div>
  );
}

export function EvaluationFiltersV2({
  filters,
  onFiltersChange,
}: EvaluationFiltersV2Props) {
  const [availableAttributes, setAvailableAttributes] = useState<
    AvailableAttribute[]
  >([]);
  const [loadingAttributes, setLoadingAttributes] = useState(false);

  useEffect(() => {
    const fetchAttributes = async () => {
      setLoadingAttributes(true);
      try {
        const { data, error } = await supabase
          .from("evaluation_form_items")
          .select("id, label, evaluation_forms!inner(name, is_active)")
          .eq("evaluation_forms.is_active", true);
        if (error) {
          console.error("Erro ao carregar atributos:", error);
          return;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const rows = (data ?? []) as any[];
        const bySlug = new Map<string, AvailableAttribute>();
        rows.forEach((item) => {
          const slug = (item.label as string)
            .toLowerCase()
            .replace(/ /g, "_")
            .replace(/-/g, "_");
          if (!bySlug.has(slug)) {
            bySlug.set(slug, {
              slug,
              label: item.label,
              formulario: item.evaluation_forms?.name || "Formulário",
            });
          }
        });
        setAvailableAttributes(
          [...bySlug.values()].sort((a, b) =>
            a.label.localeCompare(b.label, "pt-BR"),
          ),
        );
      } catch (err) {
        console.error("Erro ao buscar atributos:", err);
      } finally {
        setLoadingAttributes(false);
      }
    };
    fetchAttributes();
  }, []);

  const attributeOptions = useMemo(
    () =>
      availableAttributes.map((attr) => ({
        value: attr.slug,
        label: `${attr.label} (${attr.formulario})`,
      })),
    [availableAttributes],
  );

  const selectedAttributes = filters.atributos_selecionados ?? [];
  const selectedSlugs = selectedAttributes.map((attr) => attr.slug);

  const setStatus = (
    field: "avaliado_perfil" | "avaliado_projeto",
    value: string,
  ) => {
    onFiltersChange({
      ...filters,
      [field]: value === "" ? undefined : (value as StatusAvaliacao),
    });
  };

  const setMin = (
    field: "min_media_projeto" | "min_media_perfil" | "min_maior_nota",
    value: number | undefined,
  ) => {
    onFiltersChange({ ...filters, [field]: value });
  };

  const handleAttributesChange = (slugs: string[]) => {
    const next: AtributoFiltro[] = slugs.map((slug) => {
      const existing = selectedAttributes.find((attr) => attr.slug === slug);
      if (existing) return existing;
      const attribute = availableAttributes.find((attr) => attr.slug === slug);
      return {
        slug,
        label: attribute?.label ?? slug,
        min_valor: DEFAULT_MIN_VALUE,
      };
    });
    onFiltersChange({
      ...filters,
      atributos_selecionados: next.length > 0 ? next : undefined,
    });
  };

  const handleAttributeMinChange = (slug: string, minValue: number) => {
    onFiltersChange({
      ...filters,
      atributos_selecionados: selectedAttributes.map((attr) =>
        attr.slug === slug ? { ...attr, min_valor: minValue } : attr,
      ),
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <FieldLabel>
          <Briefcase className="h-3.5 w-3.5 text-blue-500" />
          Avaliado por projeto
        </FieldLabel>
        <SegmentedFilter
          options={STATUS_OPTIONS}
          value={filters.avaliado_projeto ?? ""}
          onChange={(value) => setStatus("avaliado_projeto", value)}
        />
      </div>

      <div>
        <FieldLabel>
          <User className="h-3.5 w-3.5 text-purple-500" />
          Avaliado por perfil
        </FieldLabel>
        <SegmentedFilter
          options={STATUS_OPTIONS}
          value={filters.avaliado_perfil ?? ""}
          onChange={(value) => setStatus("avaliado_perfil", value)}
        />
      </div>

      <MinScoreSlider
        icon={<Briefcase className="h-3.5 w-3.5 text-blue-500" />}
        label="Média mínima (projeto)"
        hint="Nota dada por agências após projetos"
        value={filters.min_media_projeto}
        onChange={(value) => setMin("min_media_projeto", value)}
      />

      <MinScoreSlider
        icon={<User className="h-3.5 w-3.5 text-purple-500" />}
        label="Média mínima (perfil)"
        hint="Nota dos avaliadores internos"
        value={filters.min_media_perfil}
        onChange={(value) => setMin("min_media_perfil", value)}
      />

      <MinScoreSlider
        icon={<Star className="h-3.5 w-3.5 text-yellow-500" />}
        label="Maior nota mínima (qualquer)"
        hint="Já recebeu nota acima deste valor"
        value={filters.min_maior_nota}
        onChange={(value) => setMin("min_maior_nota", value)}
      />

      <div>
        <FieldLabel>Atributos específicos</FieldLabel>
        <FacetMultiSelect
          placeholder={
            loadingAttributes ? "Carregando atributos..." : "Selecionar atributos..."
          }
          searchPlaceholder="Buscar atributo..."
          options={attributeOptions}
          selected={selectedSlugs}
          onChange={handleAttributesChange}
          disabled={loadingAttributes}
          hideSelected
        />
        {selectedAttributes.length > 0 && (
          <div className="mt-2 space-y-2">
            {selectedAttributes.map((attr) => (
              <div key={attr.slug} className="flex items-center gap-2">
                <span
                  className="min-w-0 flex-1 truncate text-xs"
                  title={attr.label}
                >
                  {attr.label}
                </span>
                <span className="shrink-0 text-[11px] text-muted-foreground">
                  mín.
                </span>
                <Slider
                  value={[attr.min_valor]}
                  onValueChange={(value) =>
                    handleAttributeMinChange(attr.slug, value[0])
                  }
                  max={10}
                  step={0.5}
                  className="w-24 shrink-0"
                />
                <span className="w-8 shrink-0 rounded bg-muted px-1 py-0.5 text-center font-mono text-xs">
                  {attr.min_valor}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    handleAttributesChange(
                      selectedSlugs.filter((slug) => slug !== attr.slug),
                    )
                  }
                  aria-label={`Remover atributo ${attr.label}`}
                  className="shrink-0 text-muted-foreground hover:text-destructive"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
